// Wires @scribe/core's platform-agnostic ScribeApiClient to this app's auth (MSAL / Entra ID)
// and env config. This is the ONLY place a ScribeApiClient is constructed — screens/hooks
// pull it from ApiProvider (see App.tsx) so there is exactly one token-provider closure.
import { ScribeApiClient } from '@scribe/core/client';
import type { TokenProvider } from '@scribe/core/client';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiTokenRequest } from './authConfig';
import { isMock } from './mock';
import { isLocalAuth, LOCAL_AUTH_TOKEN } from './localAuth';

/**
 * Builds the `getToken` closure bound to a given MSAL instance — the actual auth-guard logic
 * (mock/local-dev-auth short-circuits, silent-then-interactive Entra token acquisition).
 * Extracted from `createApiClient` so it's unit-testable against a stubbed
 * `IPublicClientApplication` without needing a real ScribeApiClient/fetch round trip.
 *
 * Acquires a fresh access token for the Scribe API scope (VITE_ENTRA_API_SCOPE — the Scribe
 * API's own exposed scope, NOT a Microsoft Graph scope) on every call via
 * `acquireTokenSilent`, which uses MSAL's own cache/refresh under the hood so a near-expiry
 * token is transparently renewed. Falls back to an interactive redirect only if silent
 * acquisition truly requires it (`InteractionRequiredAuthError`).
 */
export function createTokenProvider(msalInstance: IPublicClientApplication): TokenProvider {
  return async () => {
    if (isMock()) return 'mock-token';
    if (isLocalAuth()) return LOCAL_AUTH_TOKEN;

    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (!account) return null;

    try {
      const result = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Silent acquisition needs user interaction (e.g. consent/MFA step-up) — fall back
        // to a redirect rather than surfacing raw 401s to every in-flight API call.
        await msalInstance.acquireTokenRedirect({ ...apiTokenRequest, account });
        return null;
      }
      throw err;
    }
  };
}

/**
 * Build a ScribeApiClient bound to the current MSAL instance. This is the ONLY place a
 * ScribeApiClient is constructed — screens/hooks pull it from ApiProvider (see App.tsx) so
 * there is exactly one token-provider closure.
 */
export function createApiClient(msalInstance: IPublicClientApplication): ScribeApiClient {
  // Mock mode performs no network I/O, but ScribeApiClient's constructor requires a string
  // baseUrl (it normalizes it with .replace) — .env.mock normally supplies one locally, and
  // a fresh CI checkout doesn't have that gitignored file. An .invalid-TLD placeholder keeps
  // any accidental mock-mode fetch loudly unroutable instead of silently hitting a real host.
  const baseUrl =
    import.meta.env.VITE_API_BASE_URL ?? (isMock() ? 'http://scribe-mock.invalid' : '');
  return new ScribeApiClient({
    baseUrl,
    getToken: createTokenProvider(msalInstance),
  });
}
